import { memoizeNormalizer } from "./memoized-normalization"
import { getBrowserAuthoringNormalizers } from "./browser-authoring-normalizers"

const TASK_SNAPSHOT_FIELDS = [
    "id",
    "isHeader",
    "title",
    "classification",
    "taskId",
    "componentRef",
    "linkedFields",
    "skillSet",
    "minutes",
]

// Sentinel used to preserve empty paragraphs through text normalisation so that
// structural blank-line edits (e.g. a spacer <p> between chips) are not silently
// treated as no-ops by the save-skip check.
const EMPTY_PARA_SENTINEL = "\u{1F4DC}empty_para\u{1F4DC}"

const browserSnapshotNormalizer = memoizeNormalizer(getBrowserAuthoringNormalizers().normalizeAuthoringSnapshotHtml)

function markEmptyParagraphs(html) {
    if (!html) return html
    return html.replace(/<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, EMPTY_PARA_SENTINEL)
}

function resolveSnapshotNormalizer({ normalizeHtml, normalizeSnapshotHtml }) {
    if (!normalizeHtml) return null
    return normalizeSnapshotHtml ?? browserSnapshotNormalizer
}

export function buildTaskSnapshotEntry(task, { index = null, normalizeHtml = false, normalizeSnapshotHtml } = {}) {
    const snapshotNormalizer = resolveSnapshotNormalizer({ normalizeHtml, normalizeSnapshotHtml })
    const normalizeHtmlValue = (value) =>
        snapshotNormalizer ? snapshotNormalizer(markEmptyParagraphs(value ?? "")) : value
    const build = {
        ...Object.fromEntries(TASK_SNAPSHOT_FIELDS.filter((k) => k in task).map((k) => [k, task[k]])),
        content: normalizeHtmlValue(task.content),
        note: normalizeHtmlValue(task.note),
        frequency: normaliseFrequencyInput(task.frequency),
        minutes: normaliseMinutesInput(task.minutes),
    }
    if (index != null) build.index = index
    return build
}

export function buildDraftSnapshot({
    draft,
    indexTasks = false,
    normalizeHtml = false,
    includeTasks = true,
    normalizeSnapshotHtml,
}) {
    if (!draft) return null
    const snapshotNormalizer = resolveSnapshotNormalizer({ normalizeHtml, normalizeSnapshotHtml })
    const normalizeHtmlValue = (value) =>
        snapshotNormalizer ? snapshotNormalizer(markEmptyParagraphs(value ?? "")) : value
    const intro = stripTypename(draft.intro)
    const snapshot = {
        code: draft.code,
        title: draft.title,
        unitOfMeasure: draft.unitOfMeasure,
        intro:
            normalizeHtml && intro
                ? {
                      ...intro,
                      content: normalizeHtmlValue(intro.content),
                      note: normalizeHtmlValue(intro.note),
                  }
                : intro,
        legislation: buildLegislationSnapshot(draft?.legislation),
    }

    if (includeTasks) {
        snapshot.tasks = draft.tasks?.map((task, index) =>
            buildTaskSnapshotEntry(task, {
                index: indexTasks ? index + 1 : null,
                normalizeHtml,
                normalizeSnapshotHtml,
            })
        )
    }

    return snapshot
}

export function buildLegislationSnapshot(legislation = []) {
    return (legislation ?? []).map(Number)
}

function stripTypename(obj) {
    if (!obj || typeof obj !== "object") return obj
    if (Array.isArray(obj)) return obj.map(stripTypename)
    return Object.fromEntries(
        Object.entries(obj)
            .filter(([k]) => k !== "__typename" && !k.startsWith("$"))
            .map(([k, v]) => [k, typeof v === "object" && v !== null ? stripTypename(v) : v])
    )
}

function normaliseMinutesInput(value) {
    if (value === "") return 0
    if (value === null) return value
    const number = Number(value)
    return Number.isFinite(number) ? number : value
}

function normaliseFrequencyInput(frequency) {
    const cleaned = stripTypename(frequency)
    if (!cleaned || typeof cleaned !== "object") return cleaned

    const interval = normaliseFrequencyInterval(cleaned.interval)
    return {
        interval,
        period: interval > 0 ? cleaned.period ?? "Month" : "Unknown",
    }
}

function normaliseFrequencyInterval(value) {
    if (value === "" || value == null) return 0

    const number = Number(value)
    return Number.isFinite(number) ? Math.max(0, Math.trunc(number)) : 0
}
